<template>
  <!-- API progress bar -->
  <div v-if="isLoading" class="fixed top-0 left-0 right-0 z-[300] h-[3px]">
    <div class="progress-bar h-full bg-[var(--color-primary)]" />
  </div>

  <router-view />

  <!-- Idle / session expiry warning (only when authenticated) -->
  <IdleWarningModal v-if="isAuthenticated" />

  <!-- Toast notifications -->
  <Teleport to="body">
    <div class="fixed top-4 right-4 z-[250] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
      <TransitionGroup name="toast">
        <div
          v-for="item in toasts"
          :key="item.id"
          :class="[
            'pointer-events-auto flex items-start gap-3.5 px-5 py-4 rounded-xl shadow-lg',
            item.type === 'error' ? 'bg-red-50 border border-red-100' : '',
            item.type === 'success' ? 'bg-green-50 border border-green-100' : '',
            item.type === 'warning' ? 'bg-amber-50 border border-amber-100' : ''
          ]"
        >
          <!-- Icon -->
          <div :class="['shrink-0 mt-0.5', item.type === 'error' ? 'text-red-500' : '', item.type === 'success' ? 'text-green-600' : '', item.type === 'warning' ? 'text-amber-500' : '']">
            <svg v-if="item.type === 'error'" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <svg v-else-if="item.type === 'success'" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <svg v-else class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p
              v-if="item.title"
              :class="[
                'text-[15px] font-semibold leading-snug',
                item.type === 'error' ? 'text-red-800' : '',
                item.type === 'success' ? 'text-green-800' : '',
                item.type === 'warning' ? 'text-amber-800' : ''
              ]"
            >{{ item.title }}</p>
            <p
              :class="[
                'text-[14px] leading-snug',
                item.title ? 'mt-0.5' : '',
                item.type === 'error' ? 'text-red-700' : '',
                item.type === 'success' ? 'text-green-700' : '',
                item.type === 'warning' ? 'text-amber-700' : ''
              ]"
            >{{ item.message }}</p>
          </div>
          <!-- Close -->
          <button
            @click="dismissToast(item.id)"
            :class="[
              'shrink-0 p-1 rounded-md transition-colors',
              item.type === 'error' ? 'text-red-400 hover:text-red-600 hover:bg-red-100' : '',
              item.type === 'success' ? 'text-green-400 hover:text-green-600 hover:bg-green-100' : '',
              item.type === 'warning' ? 'text-amber-400 hover:text-amber-600 hover:bg-amber-100' : ''
            ]"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>

  <!-- Connection lost overlay -->
  <Teleport to="body">
    <Transition name="overlay">
      <div v-if="!isOnline" class="fixed inset-0 z-[200] flex items-end justify-center sm:items-center p-4 bg-white/60 backdrop-blur-sm">
        <div class="w-full max-w-md bg-white rounded-2xl shadow-2xl shadow-gray-300/40 border border-gray-200/80 p-8 animate-fade-in">
          <!-- Pulsing icon -->
          <div class="flex flex-col items-center text-center mb-6">
            <div class="relative mb-4">
              <div class="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                <svg class="w-7 h-7 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23"/>
                  <path d="M16.72 11.06A10.94 10.94 0 0119 12.55"/>
                  <path d="M5 12.55a10.94 10.94 0 015.17-2.39"/>
                  <path d="M10.71 5.05A16 16 0 0122.56 9"/>
                  <path d="M1.42 9a15.91 15.91 0 014.7-2.88"/>
                  <path d="M8.53 16.11a6 6 0 016.95 0"/>
                  <line x1="12" y1="20" x2="12.01" y2="20"/>
                </svg>
              </div>
              <span class="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-red-500 animate-pulse" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900">Connection Lost</h3>
            <p class="text-sm text-gray-500 mt-1">Unable to reach the server</p>
          </div>

          <!-- Status bar -->
          <div class="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 mb-5">
            <div class="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
            <p class="text-sm text-gray-500">
              <span v-if="retrying">Retrying<span class="loading-dots" />  </span>
              <span v-else>Waiting for connection to resume...</span>
            </p>
          </div>

          <button
            @click="retryNow"
            :disabled="retrying"
            class="w-full h-11 rounded-lg text-sm font-medium bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ retrying ? 'Checking...' : 'Retry Now' }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import api from './services/api'
import session from './services/session'
import IdleWarningModal from './components/IdleWarningModal.vue'

export default {
  name: 'App',
  components: { IdleWarningModal },

  data() {
    return {
      retrying: false,
      retryInterval: null
    }
  },

  computed: {
    isOnline() {
      return this.$store.getters['connection/isOnline']
    },
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated']
    },
    isLoading() {
      return this.$store.getters['loading/isLoading']
    },
    toasts() {
      return this.$store.getters['toast/items']
    }
  },

  watch: {
    isOnline(online) {
      if (!online) {
        this.startAutoRetry()
      } else {
        this.stopAutoRetry()
      }
    },
    isAuthenticated: {
      immediate: true,
      handler(authed) {
        if (authed) session.start()
        else session.stop()
      }
    }
  },

  mounted() {
    window.addEventListener('online', this.handleBrowserOnline)
    window.addEventListener('offline', this.handleBrowserOffline)
  },

  beforeUnmount() {
    window.removeEventListener('online', this.handleBrowserOnline)
    window.removeEventListener('offline', this.handleBrowserOffline)
    this.stopAutoRetry()
    session.stop()
  },

  methods: {
    dismissToast(id) {
      this.$store.dispatch('toast/dismiss', id)
    },

    handleBrowserOffline() {
      this.$store.dispatch('connection/setOffline')
    },

    handleBrowserOnline() {
      this.retryNow()
    },

    startAutoRetry() {
      this.stopAutoRetry()
      this.retryInterval = setInterval(() => this.retryNow(), 10000)
    },

    stopAutoRetry() {
      if (this.retryInterval) {
        clearInterval(this.retryInterval)
        this.retryInterval = null
      }
    },

    async retryNow() {
      if (this.retrying) return
      this.retrying = true

      try {
        await api.get('/health', { silent: true })
      } catch {
        // If it fails, the interceptor keeps state offline.
        // If /health doesn't exist, any non-network error means server is reachable.
      }

      this.retrying = false
    }
  }
}
</script>

<style>
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.loading-dots::after {
  content: '';
  animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
  0%   { content: ''; }
  25%  { content: '.'; }
  50%  { content: '..'; }
  75%  { content: '...'; }
}

.toast-enter-active {
  transition: all 0.3s ease-out;
}
.toast-leave-active {
  transition: all 0.2s ease-in;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(40px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
.toast-move {
  transition: transform 0.3s ease;
}

.progress-bar {
  animation: progress 1.5s ease-in-out infinite;
}

@keyframes progress {
  0%   { width: 0; margin-left: 0; }
  50%  { width: 70%; margin-left: 15%; }
  100% { width: 0; margin-left: 100%; }
}
</style>
